/**
 * The one hash URL this console has to understand.
 *
 * lc-api mints handoff destinations as
 *
 *     https://ap1.cmd.bloo.io/#/auth/handoff/<token>
 *
 * — a hash route, because the app it was written against is the legacy React
 * Router v5 console, which is hash-based. This console is path-based: the
 * browser sends only `/` to the server, the fragment never reaches the router,
 * and the user lands on `/` — which, having no session yet, bounces them to
 * login. The ticket then dies unspent 60 seconds later.
 *
 * So the fragment is translated to a path once, before the router is created.
 * `replaceState`, not `assign`: no reload, and no hash URL left in the back
 * stack for Back to return to.
 *
 * DELIBERATELY NARROW. This matches the handoff route and nothing else. A
 * general "any `#/x` becomes `/x`" rewrite would capture in-page anchors — the
 * login page's own `href="#/auth/reset-password"` among them — and turn
 * ordinary links into navigations. One known contract, one rule.
 *
 * The path form (`/auth/handoff/<token>`, no fragment) is left alone: it
 * already routes, and is what the address bar shows after this runs.
 */
const HASH_HANDOFF = /^#\/auth\/handoff\/([^/?#]+)\/?$/

/**
 * @returns the path it rewrote to, or null if the URL was not a hash handoff.
 *   Returning the path (rather than nothing) is what makes this testable
 *   without a router.
 */
export function rewriteHashEntry(
  location: { hash: string } = window.location,
  history: Pick<History, 'replaceState'> = window.history,
): string | null {
  const match = HASH_HANDOFF.exec(location.hash)
  if (!match) return null
  const token = match[1]
  if (!token) return null
  const path = `/auth/handoff/${token}`
  try {
    history.replaceState(null, '', path)
  } catch {
    // A sandboxed frame can refuse replaceState. Nothing else in the boot
    // sequence depends on this having worked; the user sees the login page,
    // which is where an unusable ticket sends them anyway.
    return null
  }
  return path
}
